import { useEffect, useState } from 'react';
import type { User } from 'firebase/auth';
import {
  signInWithGoogle as firebaseSignIn,
  signOut as firebaseSignOut,
  onAuthStateChange,
} from '../lib/firebase';
import { supabase } from '../lib/supabase';
import { runMigrations } from '../lib/migrations/contentItemMigration';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: Error | null;
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    // Listen for auth changes
    const unsubscribe = onAuthStateChange(async (user) => {
      setAuthState({
        user,
        loading: false,
        error: null,
      });

      // Run migrations when user logs in
      if (user) {
        try {
          console.log('[Auth] Running data migrations...');
          await runMigrations();
          console.log('[Auth] Migrations complete');
        } catch (error) {
          console.error('[Auth] Migration error:', error);
          // Don't block login if migration fails
        }
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // Sign in with Google (popup flow)
  const signInWithGoogle = async () => {
    try {
      setAuthState((prev) => ({ ...prev, loading: true, error: null }));
      const user = await firebaseSignIn();

      // ✅ CHECK ALPHA ALLOWLIST
      console.log('[Auth] Checking allowlist for:', user.email);
      const { data, error: allowlistError } = await supabase
        .from('alpha_allowlist')
        .select('email, notes')
        .eq('email', user.email || '')
        .single();

      if (allowlistError || !data) {
        // User not in allowlist - sign them out
        console.warn('[Auth] ❌ Access denied for:', user.email);
        console.warn('[Auth] Allowlist error details:', {
          code: allowlistError?.code,
          message: allowlistError?.message,
          details: allowlistError?.details,
          hint: allowlistError?.hint,
          fullError: allowlistError
        });
        await firebaseSignOut();

        const errorMessage =
          'Access denied: NIDOnote is currently in private alpha testing.\n\n' +
          'To request access, please email dan.tolan@gmail.com with subject "Alpha Access Request".\n\n' +
          'Include your Google email address in the request.';

        throw new Error(errorMessage);
      }

      console.log('[Auth] ✅ Allowlist check passed for:', user.email);
      if (data.notes) {
        console.log('[Auth] User notes:', data.notes);
      }

      setAuthState({
        user,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error('Error signing in with Google:', error);
      setAuthState((prev) => ({
        ...prev,
        loading: false,
        error: error as Error,
      }));
      throw error;
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      setAuthState((prev) => ({ ...prev, loading: true, error: null }));
      await firebaseSignOut();
      setAuthState({
        user: null,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error('Error signing out:', error);
      setAuthState((prev) => ({
        ...prev,
        loading: false,
        error: error as Error,
      }));
      throw error;
    }
  };

  return {
    user: authState.user,
    loading: authState.loading,
    error: authState.error,
    signInWithGoogle,
    signOut,
    isAuthenticated: !!authState.user,
  };
};
